import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  isValidAnswers,
  missingCoreFields,
  type QualificationAnswers,
} from "@/lib/stackQualification.shared";

const ATTR_COLUMNS =
  "income_range, household_size_bucket, is_first_time_buyer, down_payment_savings, purchase_timeline";

/** What we already know, and which of the core three are still blank. */
export const getMyQualification = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_attributes")
      .select(ATTR_COLUMNS)
      .eq("user_id", context.userId)
      .maybeSingle();

    const answers = (data ?? {}) as QualificationAnswers;
    return { answers, missing: missingCoreFields(answers as any) };
  });

/**
 * Save one (or more) answers for the signed-in member. Closes any outstanding
 * emailed check once the core three are on file, and awards points once.
 */
export const saveMyQualification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { answers: QualificationAnswers }) => {
    if (!isValidAnswers(input?.answers)) throw new Error("Invalid answers");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { saveQualificationForUser } = await import("@/lib/stackQualification.server");
    const result = await saveQualificationForUser(context.userId, data.answers);
    if (!result.ok) throw new Error(result.error);

    if (result.complete) {
      // Idempotent server-side: the RPC dedupes on the key.
      const { error: pointsError } = await (context.supabase as any).rpc("award_phase3_points", {
        _event_key: "stack_qualification_complete",
        _dedupe_key: context.userId,
        _points: 25,
      });
      if (pointsError) {
        console.error("[qualification] award_phase3_points failed", pointsError.message);
      }
    }

    return { complete: result.complete };
  });
